import React, { useState, useEffect } from 'react';
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';

const ApiKeysTab = ({ onNotify }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getApiKeys();
      setKeys(data);
    } catch {
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const result = await settingsService.generateApiKey(keyName);
      setNewKey(result);
      setKeyName('');
      setShowCreate(false);
      onNotify('API key created successfully');
      loadKeys();
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to create API key', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, name) => {
    if (!window.confirm(`Revoke API key "${name}"? This cannot be undone.`)) return;
    try {
      await settingsService.revokeApiKey(keyId);
      onNotify('API key revoked');
      loadKeys();
    } catch {
      onNotify('Failed to revoke API key', 'error');
    }
  };

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onNotify('Failed to copy', 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* New Key Display Modal */}
      {newKey && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3" role="status" aria-live="polite">
            <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
            <h3 className="text-lg font-medium text-white">API Key Created</h3>
          </div>
          <p className="text-sm text-gray-300 mb-3">
            Copy your API key now. For security reasons, it will not be shown again.
          </p>
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 flex items-center justify-between">
            <code className="text-sm text-green-400 break-all">{newKey.api_key}</code>
            <button
              type="button"
              onClick={() => copyKey(newKey.api_key)}
              aria-label={copied ? 'API key copied' : 'Copy API key'}
              className="ml-3 flex-shrink-0 text-blue-400 hover:text-blue-300"
            >
              {copied ? <CheckCircleIcon className="h-5 w-5" aria-hidden="true" /> : <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
          <div className="mt-3 flex justify-between items-center">
            <span className="text-xs text-gray-500">Name: {newKey.name}</span>
            <button
              onClick={() => setNewKey(null)}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-1.5 rounded-lg text-sm transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Create Key Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4">Create New API Key</h3>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label htmlFor="api-key-name" className="block text-sm font-medium text-gray-300 mb-2">Key Name</label>
              <input
                id="api-key-name"
                name="api-key-name"
                type="text"
                autoComplete="off"
                spellCheck={false}
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                required
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Production, Development"
              />
            </div>
            <button
              type="submit"
              disabled={creating}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors motion-reduce:transition-none"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* API Keys List */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <KeyIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
            <h3 className="text-lg font-medium text-white">API Keys</h3>
            <span className="text-sm text-gray-500">({keys.length})</span>
          </div>
          {!showCreate && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors motion-reduce:transition-none"
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              Generate Key
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8" role="status" aria-label="Loading API keys">
            <div className="animate-spin motion-reduce:animate-none rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : keys.length === 0 ? (
          <div className="text-center py-12">
            <KeyIcon className="h-12 w-12 text-gray-600 mx-auto mb-3" aria-hidden="true" />
            <p className="text-gray-400">No API keys yet</p>
            <p className="text-gray-500 text-sm mt-1">Generate an API key to access the platform programmatically.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {keys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <KeyIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-medium text-white">{key.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <code className="text-xs text-gray-500 font-mono">{key.key_prefix}...</code>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        key.is_active
                          ? 'bg-green-900/30 text-green-400'
                          : 'bg-red-900/30 text-red-400'
                      }`}>
                        {key.is_active ? 'Active' : 'Revoked'}
                      </span>
                      {key.last_used && (
                        <span className="text-xs text-gray-600">
                          Last used: {new Date(key.last_used).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-600">
                    Created: {new Date(key.created_at).toLocaleDateString()}
                  </span>
                  {key.is_active && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(key.id, key.name)}
                      className="text-red-400 hover:text-red-300 p-1"
                      aria-label={`Revoke API key ${key.name}`}
                      title="Revoke key"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeysTab;
